"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../ThemeProvider/utils");
var _utils2 = require("../utils");
var _ViewportProvider = require("../ViewportProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * @typedef {import('../utils/a11y/semantics').TextTag} TextTag
 */const [selectContainerProps, selectedContainerPropTypes] = (0, _utils2.selectSystemProps)([_utils2.a11yProps, _utils2.viewProps]);
const [selectTextProps, selectedTextPropTypes] = (0, _utils2.selectSystemProps)([_utils2.textProps]);
const selectTextStyles = (_ref, themeOptions) => {
  let {
    fontWeight,
    fontSize,
    color,
    lineHeight,
    fontName,
    textAlign,
    textTransform,
    letterSpacing,
    textDecorationLine,
    gradient
  } = _ref;
  return (0, _utils.applyTextStyles)({
    fontWeight,
    fontSize,
    color,
    lineHeight,
    fontName,
    themeOptions,
    textAlign,
    textTransform,
    letterSpacing,
    textDecorationLine,
    gradient
  });
};
const HALF_FONT_SIZE = 2;
const QUARTER_FONT_SIZE = 4;
const selectMobileSubSupStyles = (_ref2, type) => {
  let {
    fontSize
  } = _ref2;
  return {
    fontSize: fontSize / HALF_FONT_SIZE,
    lineHeight: fontSize,
    position: 'relative',
    top: type === 'sub' ? fontSize / QUARTER_FONT_SIZE : -fontSize / QUARTER_FONT_SIZE
  };
};

// General-purpose flexible theme-neutral base component for text
const Typography = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    children,
    variant,
    heading,
    tag = typeof heading === 'string' ? heading : undefined,
    accessibilityRole = heading === true ? 'header' : undefined,
    block = false,
    align = _Platform.default.OS === 'web' ? 'inherit' : undefined,
    tokens,
    dataSet,
    strikeThrough = false,
    ...rest
  } = _ref3;
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    themeOptions
  } = (0, _ThemeProvider.useTheme)();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? _ThemeProvider.useResponsiveThemeTokens : _ThemeProvider.useThemeTokens;
  const themeTokens = useTokens('Typography', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  const maxFontSizeMultiplier = enableMediaQueryStyleSheet ? (0, _utils2.getMaxFontMultiplier)(themeTokens[viewport]) : (0, _utils2.getMaxFontMultiplier)(themeTokens);
  const textDecorationLine = strikeThrough ? 'line-through' : 'none';
  const generateGradientString = gradientObj => {
    let gradientStr = '';
    const {
      type,
      angle,
      stops: [stopOne, stopTwo],
      specification
    } = gradientObj;
    if (type === 'radial') {
      gradientStr = `radial-gradient(${specification}`;
      gradientStr += `, ${stopOne.color} ${stopOne.stop * 100}%, ${stopTwo.color} ${stopTwo.stop * 100}%`;
    } else if (type === 'linear') {
      gradientStr = `linear-gradient(${angle}deg`;
      if (angle === 135) {
        gradientStr += `, ${stopOne.color}, 75%, ${stopTwo.color}`;
      } else {
        gradientStr += `, ${stopOne.color}, ${stopTwo.color}`;
      }
    }
    gradientStr += ')';
    return gradientStr;
  };
  const getStyleWithGradient = styles => {
    return styles.gradient ? {
      ...styles,
      color: 'transparent',
      backgroundImage: generateGradientString(styles.gradient),
      backgroundClip: 'text'
    } : styles;
  };

  /**
   * Added to maintain previous behavior, as the 'auto' value for the 'align' prop now defaults to 'inherit' on the web,
   * maintaining the previous behavior that was changed in the Text component of react-native.
   */
  const resolvedAlign = _Platform.default.OS === 'web' && align === 'auto' ? 'inherit' : align;
  let textStyles;
  let mediaIds;
  if (enableMediaQueryStyleSheet) {
    const transformedThemeTokens = Object.entries(themeTokens).reduce((acc, _ref4) => {
      let [vp, viewportTokens] = _ref4;
      acc[vp] = selectTextStyles({
        textAlign: resolvedAlign,
        textDecorationLine,
        ...viewportTokens
      }, themeOptions);
      if (themeTokens[vp].gradient) {
        const tokensWithGradient = {
          ...acc[vp],
          color: 'transparent',
          backgroundImage: generateGradientString(themeTokens[vp].gradient),
          backgroundClip: 'text'
        };
        return {
          ...acc,
          [vp]: tokensWithGradient
        };
      }
      return acc;
    }, {});
    const mediaQueryStyles = (0, _utils2.createMediaQueryStyles)(transformedThemeTokens);
    const {
      ids,
      styles
    } = _utils2.StyleSheet.create({
      text: mediaQueryStyles
    });
    textStyles = styles.text;
    mediaIds = ids.text;
  } else {
    textStyles = selectTextStyles({
      textAlign: resolvedAlign,
      textDecorationLine,
      ...themeTokens
    }, themeOptions);
  }
  const resolvedTextProps = {
    ...selectTextProps(rest),
    maxFontSizeMultiplier
  };
  const containerProps = {
    accessibilityRole,
    ...(0, _utils2.getA11yPropsFromHtmlTag)(tag, accessibilityRole),
    ...selectContainerProps(rest)
  };
  const resetTagStyling = child => {
    if (typeof child !== 'object' || !(child?.type === 'sub' || child?.type === 'sup')) {
      return child;
    }
    const childStyles = child?.props?.style || {};
    const supFontSize = childStyles.fontSize ?? themeTokens.superScriptFontSize;
    const isMobile = _Platform.default.OS === 'ios' || _Platform.default.OS === 'android';
    const isSubSup = child?.type === 'sub' || child?.type === 'sup';
    const mobileStyles = isMobile && isSubSup ? selectMobileSubSupStyles(themeTokens, child?.type) : {};
    const defaultStyles = !isMobile && isSubSup ? {
      fontSize: supFontSize,
      lineHeight: 0
    } : {};
    const sanitizedChild = /*#__PURE__*/_react.default.cloneElement(isMobile && isSubSup ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
        style: [childStyles, mobileStyles],
        children: child.props.children
      })
    }) : child, {
      style: {
        ...childStyles,
        ...defaultStyles
      }
    });
    return sanitizedChild;
  };
  const sanitizeChildren = () => {
    if (Array.isArray(children)) {
      return children.map(resetTagStyling);
    }
    return resetTagStyling(children);
  };
  const dataSetValue = mediaIds ? {
    media: mediaIds,
    ...dataSet
  } : dataSet;
  return block ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    ref: ref,
    ...containerProps,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
      ...resolvedTextProps,
      style: getStyleWithGradient(textStyles),
      dataSet: dataSetValue,
      children: sanitizeChildren(children)
    })
  }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
    ref: ref,
    ...containerProps,
    ...resolvedTextProps,
    style: getStyleWithGradient(textStyles),
    dataSet: dataSetValue,
    children: sanitizeChildren(children)
  });
});
Typography.displayName = 'Typography';
Typography.propTypes = {
  ...selectedContainerPropTypes,
  ...selectedTextPropTypes,
  tokens: (0, _utils2.getTokensPropType)('Typography'),
  variant: _utils2.variantProp.propType,
  /**
   * Renders the text with "text-decoration: 'line-through'" applied.
   */
  strikeThrough: _propTypes.default.bool,
  /**
   * Renders the text as a semantic heading. If a html heading tag is provided, uses that tag on Web.
   *
   * Does not affect styling: <Typography heading="h2"> will render a <h2> that looks like plain text.
   * Use both `heading` and `variant` props to render semantic headings that look like headings.
   *
   * In native apps, if this is `true` or any html heading tag string and accessibilityRole prop
   * is not defined, the accessibilityRole will default to "heading".
   */
  heading: _propTypes.default.oneOf([..._utils2.headingTags, true]),
  /**
   * Optional semantic HTML tag, to apply to the text on web. Does not change styling.
   *
   * `tag` and `heading` props set the same thing, so shouldn't be used together (`tag` overrides `heading`).
   *
   * In native apps, if a header tag is provided ("h1", "h2" etc) and accessibilityRole prop
   * is not defined, the accessibilityRole will default to "heading".
   */
  tag: _propTypes.default.oneOf(_utils2.textTags),
  /**
   * If true, forces the element to behave as a View block even if nested in other text
   */
  block: _propTypes.default.bool,
  /**
   * Works the same as css text-align, note that RN doesn't support "start" or "end" (see https://reactnative.dev/docs/text-style-props#textalign)
   * Justify is known to make text harder to read, especially for people with dyslexia (see https://developer.mozilla.org/en-US/docs/Web/CSS/text-align#accessibility_concerns)
   */
  align: _propTypes.default.oneOf(['auto', 'left', 'right', 'center', 'justify']),
  /**
   * Same children allowed as a React Native <Text> node
   */
  children: _propTypes.default.node,
  /**
   * Data attributes to be added to the element
   */
  dataSet: _propTypes.default.object
};
var _default = exports.default = Typography;